<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useCartStore } from "/src/stores/useCartStore.js";
import { useMeta } from "quasar";

useMeta(() => {
  return {
    title: "Terms of Service — SML Global & Oraklenz",
    meta: {
      description: {
        name: "description",
        content:
          "Terms of Service and legal agreement governing the use of SML Global and Oraklenz websites and services.",
      },
    },
  };
});

const cartStore = useCartStore();
const isCartOpen = ref(false);
const isCheckoutStep = ref(false);
const checkoutForm = ref({
  name: "",
  email: "",
  address: "",
  card: "",
  exp: "",
  cvv: "",
});

const activeSection = ref("acceptance");

const sections = [
  { id: "acceptance", label: "Acceptance of Terms" },
  { id: "description", label: "Description of Services" },
  { id: "registration", label: "Registration & Eligibility" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "account-security", label: "Account & Password Security" },
  { id: "conduct", label: "User Conduct & Prohibited Uses" },
  { id: "export-compliance", label: "Global Use & Export Laws" },
  { id: "content-license", label: "Content License & Rights" },
  { id: "contributions", label: "Contributions & Feedback" },
  { id: "indemnity", label: "Indemnity" },
  { id: "commercial-reuse", label: "Commercial Reuse of Services" },
  { id: "storage-practices", label: "General Storage & Limits" },
  { id: "modifications", label: "Modifications of Services" },
  { id: "termination", label: "Termination Policy" },
  { id: "advertisers-links", label: "Advertisers & Third-Party Links" },
  { id: "proprietary-rights", label: "Proprietary Rights & Software" },
  { id: "warranty-disclaimers", label: "Warranty Disclaimers" },
  { id: "limitation-liability", label: "Limitation of Liability & Release" },
  { id: "financial-admonition", label: "Financial Matters Admonition" },
  { id: "exclusions", label: "Exclusions & Limitations" },
  { id: "notices-beneficiaries", label: "Notices & Third-Party Rights" },
  { id: "trademarks", label: "Trademark Information" },
  { id: "copyright-claims", label: "Copyright Infringement (DMCA)" },
  { id: "general-info", label: "General Legal Information" },
  { id: "violations", label: "Reporting Violations" },
];

const scrollToSection = (id) => {
  activeSection.value = id;
  const element = document.getElementById(id);
  if (element) {
    const yOffset = -90;
    const y =
      element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

const handleScroll = () => {
  const scrollPosition = window.scrollY + 120;
  for (let i = sections.length - 1; i >= 0; i--) {
    const el = document.getElementById(sections[i].id);
    if (el && el.offsetTop <= scrollPosition) {
      activeSection.value = sections[i].id;
      break;
    }
  }
};

onMounted(() => {
  window.addEventListener("scroll", handleScroll, { passive: true });
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});

const removeFromCart = (key) => {
  if (typeof cartStore.remove === "function") {
    cartStore.remove(key);
  }
};

const checkout = () => {
  if (cartStore.items.length === 0) {
    alert("Your cart is empty.");
    return;
  }
  isCheckoutStep.value = true;
};

const completeOrder = () => {
  alert("Order placed successfully! This is a placeholder.");
  if (typeof cartStore.items !== "undefined") {
    cartStore.items = [];
  }
  isCheckoutStep.value = false;
  isCartOpen.value = false;
};

const handleDialogHide = () => {
  isCheckoutStep.value = false;
};
</script>

<template>
  <q-page class="legal-page">
    <!-- Announcement Bar -->
    <div id="announce_id" class="announce">
      <b>Same active ingredient as leading perio gels.</b> ~20% less per tube.
      No subscription required.
    </div>

    <!-- Hero / Title Section -->
    <section class="legal-hero">
      <div class="wrap">
        <div class="breadcrumbs">
          <router-link to="/"> Home </router-link>
          <span class="separator">/</span>
          <span class="current">Terms of Service</span>
        </div>

        <div class="hero-content">
          <span class="eyebrow">Legal & Compliance</span>
          <h1 class="page-title">Terms of Service Agreement</h1>
          <p class="hero-subtitle">
            Please read these terms carefully before accessing or using our
            website, products, and services.
          </p>

          <div class="meta-badge-row">
            <div class="meta-badge">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span>Effective Date: <strong>01/01/2020</strong></span>
            </div>
            <div class="meta-badge">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>Space Maintainers Labs, Inc. (SML Global)</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Notice Banner -->
    <div class="wrap q-my-lg">
      <div class="notice-callout">
        <div class="callout-icon">
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div class="callout-text">
          <strong>PLEASE READ CAREFULLY:</strong> By accessing or using our
          sites and our services, you hereby agree to be bound by the terms and
          all terms incorporated herein by reference. It is your responsibility
          as a user, customer, or prospective customer to review these terms
          before proceeding. If you do not expressly agree to all terms and
          conditions, please do not access or use our sites or services.
        </div>
      </div>
    </div>

    <!-- Main Content Layout with Sticky Sidebar -->
    <div class="wrap legal-body-layout">
      <!-- Sticky Navigation Sidebar -->
      <aside class="legal-sidebar">
        <div class="sidebar-inner">
          <div class="sidebar-header">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="8" y1="6" x2="21" y2="6" />
              <line x1="8" y1="12" x2="21" y2="12" />
              <line x1="8" y1="18" x2="21" y2="18" />
              <line x1="3" y1="6" x2="3.01" y2="6" />
              <line x1="3" y1="12" x2="3.01" y2="12" />
              <line x1="3" y1="18" x2="3.01" y2="18" />
            </svg>
            <span>Table of Contents</span>
          </div>

          <nav class="toc-list">
            <button
              v-for="(item, index) in sections"
              :key="item.id"
              class="toc-link"
              :class="{ active: activeSection === item.id }"
              @click="scrollToSection(item.id)"
            >
              <span class="toc-num">{{ index + 1 }}.</span>
              <span class="toc-label">{{ item.label }}</span>
            </button>
          </nav>
        </div>
      </aside>

      <!-- Legal Content Articles -->
      <main class="legal-content">
        <!-- 1. ACCEPTANCE OF TERMS -->
        <section id="acceptance" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 1</div>
            <h2>Acceptance of Terms</h2>
          </div>
          <div class="card-content">
            <p>
              The following Terms of Service Agreement (the
              "<strong>TOS</strong>") is a legally binding agreement that shall
              govern the relationship with our users and others which may
              interact or interface with
              <strong>Space Maintainers Labs, Inc.</strong>, also known as
              <strong>SML Global</strong>, located at:
            </p>

            <div class="company-address-box">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0077be"
                stroke-width="2"
              >
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              <div>
                <strong>Space Maintainers Labs, Inc. (SML Global)</strong><br />
                9129 Lurline Avenue, Chatsworth, California 91311
              </div>
            </div>

            <p>
              and our subsidiaries and affiliates, in association with the use
              of the SML Global website, which includes
              <a href="https://smlglobal.com" target="_blank" rel="noopener"
                >https://smlglobal.com</a
              >
              (the "<strong>Site</strong>") and its Services, which shall be
              defined below.
            </p>
          </div>
        </section>

        <!-- 2. DESCRIPTION OF WEBSITE SERVICES -->
        <section id="description" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 2</div>
            <h2>Description of Website Services Offered</h2>
          </div>
          <div class="card-content">
            <p>
              The Site is an e-commerce website which has the following
              description:
            </p>
            <blockquote class="highlight-quote">
              "Offer dental appliances and supplies, including events &amp;
              education."
            </blockquote>
            <p>
              Any and all visitors to our site, despite whether they are
              registered or not, shall be deemed as "<strong>users</strong>" of
              the herein contained Services provided for the purpose of this
              TOS. Once an individual registers for our Services, through the
              process of creating an account, the user shall then be considered
              a "<strong>member</strong>."
            </p>
            <p>
              The user and/or member acknowledges and agrees that the Services
              provided and made available through our website and applications,
              which may include some mobile applications and that those
              applications may be made available on various social media
              networking sites and numerous other platforms and downloadable
              programs, are the sole property of
              <strong>Space Maintainers Labs, Inc.</strong>.
            </p>
            <p>
              At its discretion, Space Maintainers Labs, Inc. may offer
              additional website Services and/or products, or update, modify or
              revise any current content and Services, and this Agreement shall
              apply to any and all additional Services and/or products and any
              and all updated, modified or revised Services unless otherwise
              stipulated. Space Maintainers Labs, Inc. does hereby reserve the
              right to cancel and cease offering any of the aforementioned
              Services and/or products.
            </p>
            <p>
              You, as the end user and/or member, acknowledge, accept and agree
              that Space Maintainers Labs, Inc. shall not be held liable for any
              such updates, modifications, revisions, suspensions or
              discontinuance of any of our Services and/or products. Your
              continued use of the Services provided, after such posting of any
              updates, changes, and/or modifications shall constitute your
              acceptance of such updates, changes and/or modifications, and as
              such, frequent review of this Agreement and any and all applicable
              terms and policies should be made by you to ensure you are aware
              of all terms and policies currently in effect. Should you not
              agree to the updated, revised or modified terms, you must stop
              using the provided Services forthwith.
            </p>
            <p>
              Furthermore, the user and/or member understands, acknowledges and
              agrees that the Services offered shall be provided
              <strong>"AS IS"</strong> and as such Space Maintainers Labs, Inc.
              shall not assume any responsibility or obligation for the
              timeliness, missed delivery, deletion and/or any failure to store
              user content, communication or personalization settings.
            </p>
          </div>
        </section>

        <!-- 3. REGISTRATION -->
        <section id="registration" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 3</div>
            <h2>Registration</h2>
          </div>
          <div class="card-content">
            <p>
              To register and become a "member" of the Site, you must be at
              least <strong>18 years of age</strong> to enter into and form a
              legally binding contract. In addition, you must be in good
              standing and not an individual that has been previously barred
              from receiving SML Global's Services under the laws and statutes
              of the United States or other applicable jurisdiction.
            </p>
            <p>
              When you register, SML Global may collect information such as your
              name, e-mail address, birth date, gender, mailing address,
              occupation, industry and personal interests. You can edit your
              account information at any time. Once you register with SML Global
              and sign in to our Services, you are no longer anonymous to us.
            </p>
            <p>
              Furthermore, the registering party hereby acknowledges,
              understands and agrees to:
            </p>
            <ul class="styled-list">
              <li>
                Furnish factual, correct, current and complete information with
                regards to yourself as may be requested by the data registration
                process, and
              </li>
              <li>
                Maintain and promptly update your registration and profile
                information in an effort to maintain accuracy and completeness
                at all times.
              </li>
            </ul>
            <p>
              If anyone knowingly provides any information of a false, untrue,
              inaccurate or incomplete nature, Space Maintainers Labs, Inc. will
              have sufficient grounds and rights to suspend or terminate the
              member in violation of this aspect of the Agreement, and as such
              refuse any and all current or future use of Space Maintainers
              Labs, Inc. Services, or any portion thereof.
            </p>
            <div class="sub-alert-box">
              <h4>Safety &amp; Privacy of Children</h4>
              <p>
                It is Space Maintainers Labs, Inc.'s priority to ensure the
                safety and privacy of all its visitors, users and members,
                especially that of children. Therefore, it is for this reason
                that the parents of any child under the age of 13 that permit
                their child or children access to the SML Global website
                platform Services must create a "family" account, which will
                certify that the individual creating the "family" account is of
                18 years of age and as such, the parent or legal guardian of any
                child or children registered under the "family" account.
              </p>
              <p>
                As the creator of the "family" account, s/he is thereby granting
                permission for his/her child or children to access the various
                Services provided, including, but not limited to, message
                boards, email, and/or instant messaging. It is the parent's
                and/or legal guardian's responsibility to determine whether any
                of the Services and/or content provided are age-appropriate for
                his/her child.
              </p>
            </div>
          </div>
        </section>

        <!-- 4. PRIVACY POLICY -->
        <section id="privacy" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 4</div>
            <h2>Privacy Policy</h2>
          </div>
          <div class="card-content">
            <p>
              Every member's registration data and various other personal
              information are strictly protected by the Space Maintainers Labs,
              Inc. Online Privacy Policy (see the full
              <router-link to="/privacy-policy"> Privacy Policy </router-link>).
            </p>
            <p>
              As a member, you herein consent to the collection and use of the
              information provided, including the transfer of information within
              the United States and/or other countries for storage, processing
              or use by Space Maintainers Labs, Inc. and/or our subsidiaries and
              affiliates.
            </p>
          </div>
        </section>

        <!-- 5. MEMBER ACCOUNT, USERNAME, PASSWORD AND SECURITY -->
        <section id="account-security" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 5</div>
            <h2>Member Account, Username, Password and Security</h2>
          </div>
          <div class="card-content">
            <p>
              When you set up an account, you are the sole authorized user of
              your account. You shall be responsible for maintaining the secrecy
              and confidentiality of your password and for all activities that
              transpire on or within your account. It is your responsibility for
              any act or omission of any user(s) that access your account
              information that, if undertaken by you, would be deemed a
              violation of the TOS.
            </p>
            <p>
              It shall be your responsibility to notify Space Maintainers Labs,
              Inc. immediately if you notice any unauthorized access or use of
              your account or password or any other breach of security. Space
              Maintainers Labs, Inc. shall not be held liable for any loss
              and/or damage arising from any failure to comply with this term
              and/or condition of the TOS.
            </p>
          </div>
        </section>

        <!-- 6. CONDUCT -->
        <section id="conduct" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 6</div>
            <h2>Conduct</h2>
          </div>
          <div class="card-content">
            <p>
              As a user or member of the Site, you herein acknowledge,
              understand and agree that all information, text, software, data,
              photographs, music, video, messages, tags or any other content,
              whether it is publicly or privately posted and/or transmitted, is
              the expressed sole responsibility of the individual from whom the
              content originated.
            </p>
            <p>
              In short, this means that you are solely responsible for any and
              all content posted, uploaded, emailed, transmitted or otherwise
              made available by way of the SML Global Services, and as such, we
              do not guarantee the accuracy, integrity or quality of such
              content. It is expressly understood that by use of our Services,
              you may be exposed to content including, but not limited to, any
              errors or omissions in any content posted, and/or any loss or
              damage of any kind incurred as a result of the use of any content
              posted, emailed, transmitted or otherwise made available by SML
              Global.
            </p>
            <p>
              Furthermore, you herein agree not to make use of Space Maintainers
              Labs, Inc.'s Services for the purpose of:
            </p>

            <ul class="styled-list conduct-list">
              <li>
                Uploading, posting, emailing, transmitting, or otherwise making
                available any content that shall be deemed unlawful, harmful,
                threatening, abusive, harassing, tortious, defamatory, vulgar,
                obscene, libelous, or invasive of another's privacy or which is
                hateful, and/or racially, ethnically, or otherwise
                objectionable;
              </li>
              <li>Causing harm to minors in any manner whatsoever;</li>
              <li>
                Impersonating any individual or entity, including, but not
                limited to, any SML Global officials, forum leaders, guides or
                hosts or falsely stating or otherwise misrepresenting any
                affiliation with an individual or entity;
              </li>
              <li>
                Forging captions, headings or titles or otherwise offering any
                content that you personally have no right to pursuant to any law
                nor having any contractual or fiduciary relationship with;
              </li>
              <li>
                Uploading, posting, emailing, transmitting or otherwise offering
                any such content that may infringe upon any patent, copyright,
                trademark, or any other proprietary or intellectual rights of
                any other party;
              </li>
              <li>
                Uploading, posting, emailing, transmitting or otherwise offering
                any content that you do not personally have any right to offer
                pursuant to any law or in accordance with any contractual or
                fiduciary relationship;
              </li>
              <li>
                Uploading, posting, emailing, transmitting, or otherwise
                offering any unsolicited or unauthorized advertising,
                promotional flyers, "junk mail," "spam," or any other form of
                solicitation, except in any such areas that may have been
                designated for such purpose;
              </li>
              <li>
                Uploading, posting, emailing, transmitting, or otherwise
                offering any source that may contain a software virus or other
                computer code, any files and/or programs which have been
                designed to interfere, destroy and/or limit the operation of any
                computer software, hardware, or telecommunication equipment;
              </li>
              <li>
                Disrupting the normal flow of communication, or otherwise acting
                in any manner that would negatively affect other users' ability
                to participate in any real time interactions;
              </li>
              <li>
                Interfering with or disrupting any Space Maintainers Labs, Inc.
                Services, servers and/or networks that may be connected or
                related to our website, including, but not limited to, the use
                of any device software and/or routine to bypass the robot
                exclusion headers;
              </li>
              <li>
                Intentionally or unintentionally violating any local, state,
                federal, national or international law, including, but not
                limited to, rules, guidelines, and/or regulations decreed by the
                U.S. Securities and Exchange Commission, in addition to any
                rules of any nation or other securities exchange, that would
                include without limitation, the New York Stock Exchange, the
                American Stock Exchange, or the NASDAQ, and any regulations
                having the force of law;
              </li>
              <li>
                Providing informational support or resources, concealing and/or
                disguising the character, location, and or source to any
                organization delegated by the United States government as a
                "foreign terrorist organization" in accordance to Section 219 of
                the Immigration Nationality Act;
              </li>
              <li>
                "Stalking" or with the intent to otherwise harass another
                individual; and/or
              </li>
              <li>
                Collecting or storing of any personal data relating to any other
                member or user in connection with the prohibited conduct and/or
                activities which have been set forth in the aforementioned
                paragraphs.
              </li>
            </ul>

            <p>
              Space Maintainers Labs, Inc. herein reserves the right to
              pre-screen, refuse and/or delete any content currently available
              through our Services. In addition, we reserve the right to remove
              and/or delete any such content that would violate the TOS or which
              would otherwise be considered offensive to other visitors, users
              and/or members.
            </p>
            <p>
              Space Maintainers Labs, Inc. herein reserves the right to access,
              preserve and/or disclose member account information and/or content
              if it is requested to do so by law or in good faith belief that
              any such action is deemed reasonably necessary for:
            </p>
            <ul class="styled-list">
              <li>Compliance with any legal process;</li>
              <li>Enforcement of the TOS;</li>
              <li>
                Responding to any claim that therein contained content is in
                violation of the rights of any third party;
              </li>
              <li>Responding to requests for customer service; or</li>
              <li>
                Protecting the rights, property or the personal safety of Space
                Maintainers Labs, Inc., its visitors, users and members,
                including the general public.
              </li>
            </ul>
            <p>
              Space Maintainers Labs, Inc. herein reserves the right to include
              the use of security components that may permit digital information
              or material to be protected, and that such use of information
              and/or material is subject to usage guidelines and regulations
              established by Space Maintainers Labs, Inc. or any other content
              providers supplying content services to Space Maintainers Labs,
              Inc.. You are hereby prohibited from making any attempt to
              override or circumvent any of the embedded usage rules in our
              Services. Furthermore, unauthorized reproduction, publication,
              distribution, or exhibition of any information or materials
              supplied by our Services, despite whether done so in whole or in
              part, is expressly prohibited.
            </p>
          </div>
        </section>

        <!-- 7. CAUTIONS FOR GLOBAL USE AND EXPORT/IMPORT COMPLIANCE -->
        <section id="export-compliance" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 7</div>
            <h2>Cautions for Global Use and Export and Import Compliance</h2>
          </div>
          <div class="card-content">
            <p>
              Due to the global nature of the internet, through the use of our
              network you hereby agree to comply with all local rules relating
              to online conduct and that which is considered acceptable Content.
              Uploading, posting and/or transferring of software, technology and
              other technical data may be subject to the export and import laws
              of the United States and possibly other countries.
            </p>
            <p>
              Through the use of our network, you thus agree to comply with all
              applicable export and import laws, statutes and regulations,
              including, but not limited to, the Export Administration
              Regulations (<a
                href="http://www.access.gpo.gov/bis/ear/ear_data.html"
                target="_blank"
                rel="noopener"
                >EAR Regulations</a
              >), as well as the sanctions control program of the United States
              (<a
                href="http://www.treasury.gov/resourcecenter/sanctions/Programs/Pages/Programs.aspx"
                target="_blank"
                rel="noopener"
                >U.S. Treasury Sanctions Programs</a
              >).
            </p>
            <p>Furthermore, you state and pledge that you:</p>
            <ul class="styled-list">
              <li>
                Are not on the list of prohibited individuals which may be
                identified on any government export exclusion report (<a
                  href="http://www.bis.doc.gov/complianceandenforcement/liststocheck.htm"
                  target="_blank"
                  rel="noopener"
                  >BIS Lists to Check</a
                >) nor a member of any other government which may be part of an
                export-prohibited country identified in applicable export and
                import laws and regulations;
              </li>
              <li>
                Agree not to transfer any software, technology or any other
                technical data through the use of our network Services to any
                export-prohibited country;
              </li>
              <li>
                Agree not to use our website network Services for any military,
                nuclear, missile, chemical or biological weaponry end uses that
                would be a violation of the U.S. export laws; and
              </li>
              <li>
                Agree not to post, transfer nor upload any software, technology
                or any other technical data which would be in violation of the
                U.S. or other applicable export and/or import laws.
              </li>
            </ul>
          </div>
        </section>

        <!-- 8. CONTENT PLACED OR MADE AVAILABLE FOR COMPANY SERVICES -->
        <section id="content-license" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 8</div>
            <h2>Content Placed or Made Available for Company Services</h2>
          </div>
          <div class="card-content">
            <p>
              Space Maintainers Labs, Inc. shall not lay claim to ownership of
              any content submitted by any visitor, member, or user, nor make
              such content available for inclusion on our website Services.
              Therefore, you hereby grant and allow for Space Maintainers Labs,
              Inc. the below listed worldwide, royalty-free and non-exclusive
              licenses, as applicable:
            </p>
            <ul class="styled-list">
              <li>
                <strong>Public Area Content:</strong> The content submitted or
                made available for inclusion on the publicly accessible areas of
                Space Maintainers Labs, Inc.'s sites, the license provided to
                permit to use, distribute, reproduce, modify, adapt, publicly
                perform and/or publicly display said Content on our network
                Services is for the sole purpose of providing and promoting the
                specific area to which this content was placed and/or made
                available for viewing. This license shall be available so long
                as you are a member of Space Maintainers Labs, Inc.'s sites, and
                shall terminate at such time when you elect to discontinue your
                membership.
              </li>
              <li>
                <strong
                  >Media Files (Photos, Audio, Video &amp; Graphics):</strong
                >
                Photos, audio, video and/or graphics submitted or made available
                for inclusion on the publicly accessible areas of Space
                Maintainers Labs, Inc.'s sites, the license provided to permit
                to use, distribute, reproduce, modify, adapt, publicly perform
                and/or publicly display said Content on our network Services are
                for the sole purpose of providing and promoting the specific
                area in which this content was placed and/or made available for
                viewing. This license shall be available so long as you are a
                member of Space Maintainers Labs, Inc.'s sites and shall
                terminate at such time when you elect to discontinue your
                membership.
              </li>
              <li>
                <strong>Other Content:</strong> For any other content submitted
                or made available for inclusion on the publicly accessible areas
                of Space Maintainers Labs, Inc.'s sites, the continuous, binding
                and completely sublicensable license which is meant to permit to
                use, distribute, reproduce, modify, adapt, publish, translate,
                publicly perform and/or publicly display said content, whether
                in whole or in part, and the incorporation of any such Content
                into other works in any arrangement or medium currently used or
                later developed.
              </li>
            </ul>
            <p>
              Those areas which may be deemed "publicly accessible" areas of
              Space Maintainers Labs, Inc.'s sites are those such areas of our
              network properties which are meant to be available to the general
              public, and which would include message boards and groups that are
              openly available to both users and members.
            </p>
          </div>
        </section>

        <!-- 9. CONTRIBUTIONS TO COMPANY WEBSITE -->
        <section id="contributions" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 9</div>
            <h2>Contributions to Company Website</h2>
          </div>
          <div class="card-content">
            <p>
              Space Maintainers Labs, Inc. provides an area for our users and
              members to contribute feedback to our website. When you submit
              ideas, documents, suggestions and/or proposals
              ("<strong>Contributions</strong>") to our site, you acknowledge
              and agree that:
            </p>
            <ul class="styled-list">
              <li>
                Your contributions do not contain any type of confidential or
                proprietary information;
              </li>
              <li>
                SML Global shall not be liable or under any obligation to ensure
                or maintain confidentiality, expressed or implied, related to
                any Contributions;
              </li>
              <li>
                SML Global shall be entitled to make use of and/or disclose any
                such Contributions in any such manner as they may see fit;
              </li>
              <li>
                The contributor's Contributions shall automatically become the
                sole property of SML Global; and
              </li>
              <li>
                SML Global is under no obligation to either compensate or
                provide any form of reimbursement in any manner or nature.
              </li>
            </ul>
          </div>
        </section>

        <!-- 10. INDEMNITY -->
        <section id="indemnity" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 10</div>
            <h2>Indemnity</h2>
          </div>
          <div class="card-content">
            <p>
              All users and/or members herein agree to insure and hold
              <strong>Space Maintainers Labs, Inc.</strong>, our subsidiaries,
              affiliates, agents, employees, officers, partners and/or licensors
              blameless or not liable for any claim or demand, which may
              include, but is not limited to, reasonable attorney fees made by
              any third party which may arise from any content a member or user
              of our site may submit, post, modify, transmit or otherwise make
              available through our Services, the use of SML Global Services or
              your connection with these Services, your violations of the Terms
              of Service and/or your violation of any such rights of another
              person.
            </p>
          </div>
        </section>

        <!-- 11. COMMERCIAL REUSE OF SERVICES -->
        <section id="commercial-reuse" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 11</div>
            <h2>Commercial Reuse of Services</h2>
          </div>
          <div class="card-content">
            <p>
              The member or user herein agrees not to replicate, duplicate,
              copy, trade, sell, resell nor exploit for any commercial reason
              any part, use of, or access to SML Global's sites.
            </p>
          </div>
        </section>

        <!-- 12. USE AND STORAGE GENERAL PRACTICES -->
        <section id="storage-practices" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 12</div>
            <h2>Use and Storage General Practices</h2>
          </div>
          <div class="card-content">
            <p>
              You herein acknowledge that Space Maintainers Labs, Inc. may set
              up any such practices and/or limits regarding the use of our
              Services, without limitation of the maximum number of days that
              any email, message posting or any other uploaded content shall be
              retained by Space Maintainers Labs, Inc., nor the maximum number
              of email messages that may be sent and/or received by any member,
              the maximum volume or size of any email message that may be sent
              from or may be received by an account on our Service, the maximum
              disk space allowable that shall be allocated on Space Maintainers
              Labs, Inc.'s servers on the member's behalf, and/or the maximum
              number of times and/or duration that any member may access our
              Services in a given period of time.
            </p>
            <p>
              In addition, you also agree that Space Maintainers Labs, Inc. has
              absolutely no responsibility or liability for the removal or
              failure to maintain storage of any messages and/or other
              communications or content maintained or transmitted by our
              Services. You also herein acknowledge that we reserve the right to
              delete or remove any account that is no longer active for an
              extended period of time. Furthermore, Space Maintainers Labs, Inc.
              shall reserve the right to modify, alter and/or update these
              general practices and limits at our discretion.
            </p>
          </div>
        </section>

        <!-- 13. MODIFICATIONS -->
        <section id="modifications" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 13</div>
            <h2>Modifications</h2>
          </div>
          <div class="card-content">
            <p>
              Space Maintainers Labs, Inc. shall reserve the right at any time
              it may deem fit, to modify, alter and or discontinue, whether
              temporarily or permanently, our service, or any part thereof, with
              or without prior notice. In addition, we shall not be held liable
              to you or to any third party for any such alteration,
              modification, suspension and/or discontinuance of our Services, or
              any part thereof.
            </p>
          </div>
        </section>

        <!-- 14. TERMINATION -->
        <section id="termination" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 14</div>
            <h2>Termination</h2>
          </div>
          <div class="card-content">
            <p>
              As a member of
              <a href="https://www.smlglobal.com" target="_blank" rel="noopener"
                >https://www.smlglobal.com</a
              >, you may cancel or terminate your account, associated email
              address and/or access to our Services by submitting a cancellation
              or termination request to
              <span class="text-blue">johndoe@smlglobal.com</span>
            </p>
            <p>
              As a member, you agree that Space Maintainers Labs, Inc. may,
              without any prior written notice, immediately suspend, terminate,
              discontinue and/or limit your account, any email associated with
              your account, and access to any of our Services. The cause for
              such termination, discontinuance, suspension and/or limitation of
              access shall include, but is not limited to:
            </p>
            <ul class="styled-list">
              <li>
                Any breach or violation of our TOS or any other incorporated
                agreement, regulation and/or guideline;
              </li>
              <li>
                By way of requests from law enforcement or any other
                governmental agencies;
              </li>
              <li>
                The discontinuance, alteration and/or material modification to
                our Services, or any part thereof;
              </li>
              <li>Unexpected technical or security issues and/or problems;</li>
              <li>Any extended periods of inactivity;</li>
              <li>
                Any engagement by you in any fraudulent or illegal activities;
                and/or
              </li>
              <li>
                The nonpayment of any associated fees that may be owed by you in
                connection with your https://www.smlglobal.com account Services.
              </li>
            </ul>
            <p>
              Furthermore, you herein agree that any and all terminations,
              suspensions, discontinuances, and or limitations of access for
              cause shall be made at our sole discretion and that we shall not
              be liable to you or any other third party with regards to the
              termination of your account, associated email address and/or
              access to any of our Services.
            </p>
            <p>
              The termination of your account with https://www.smlglobal.com
              shall include any and/or all of the following:
            </p>
            <ul class="styled-list">
              <li>
                The removal of any access to all or part of the Services offered
                within https://www.smlglobal.com;
              </li>
              <li>
                The deletion of your password and any and all related
                information, files, and any such content that may be associated
                with or inside your account, or any part thereof; and
              </li>
              <li>
                The barring of any further use of all or part of our Services.
              </li>
            </ul>
          </div>
        </section>

        <!-- 15. ADVERTISERS & LINKS -->
        <section id="advertisers-links" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 15</div>
            <h2>Advertisers &amp; Third-Party Links</h2>
          </div>
          <div class="card-content">
            <h3>Advertisers</h3>
            <p>
              Any correspondence or business dealings with, or the participation
              in any promotions of, advertisers located on or through our
              Services, which may include the payment and/or delivery of such
              related goods and/or Services, and any such other term, condition,
              warranty and/or representation associated with such dealings, are
              and shall be solely between you and any such advertiser. Moreover,
              you herein agree that Space Maintainers Labs, Inc. shall not be
              held responsible or liable for any loss or damage of any nature or
              manner incurred as a direct result of any such dealings or as a
              result of the presence of such advertisers on our website.
            </p>

            <h3 class="q-mt-md">Links</h3>
            <p>
              Either Space Maintainers Labs, Inc. or any third parties may
              provide links to other websites and/or resources. Thus, you
              acknowledge and agree that we are not responsible for the
              availability of any such external sites or resources, and as such,
              we do not endorse nor are we responsible or liable for any
              content, products, advertising or any other materials, on or
              available from such third party sites or resources.
            </p>
            <p>
              Furthermore, you acknowledge and agree that Space Maintainers
              Labs, Inc. shall not be responsible or liable, directly or
              indirectly, for any such damage or loss which may be a result of,
              caused or allegedly to be caused by or in connection with the use
              of or the reliance on any such content, goods or Services made
              available on or through any such site or resource.
            </p>
          </div>
        </section>

        <!-- 16. PROPRIETARY RIGHTS -->
        <section id="proprietary-rights" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 16</div>
            <h2>Proprietary Rights</h2>
          </div>
          <div class="card-content">
            <p>
              You do hereby acknowledge and agree that Space Maintainers Labs,
              Inc.'s Services and any essential software that may be used in
              connection with our Services ("<strong>Software</strong>") shall
              contain proprietary and confidential material that is protected by
              applicable intellectual property rights and other laws.
            </p>
            <p>
              Furthermore, you herein acknowledge and agree that any Content
              which may be contained in any advertisements or information
              presented by and through our Services or by advertisers is
              protected by copyrights, trademarks, patents or other proprietary
              rights and laws. Therefore, except for that which is expressly
              permitted by applicable law or as authorized by Space Maintainers
              Labs, Inc. or such applicable licensor, you agree not to alter,
              modify, lease, rent, loan, sell, distribute, transmit, broadcast,
              publicly perform and/or created any plagiaristic works which are
              based on Space Maintainers Labs, Inc. Services (e.g. Content or
              Software), in whole or part.
            </p>
            <p>
              Space Maintainers Labs, Inc. herein has granted you personal,
              non-transferable and non-exclusive rights and/or license to make
              use of the object code or our Software on a single computer, as
              long as you do not, and shall not, allow any third party to
              duplicate, alter, modify, create or plagiarize work from, reverse
              engineer, reverse assemble or otherwise make an attempt to locate
              or discern any source code, sell, assign, sublicense, grant a
              security interest in and/or otherwise transfer any such right in
              the Software.
            </p>
            <p>
              Furthermore, you do herein agree not to alter or change the
              Software in any manner, nature or form, and as such, not to use
              any modified versions of the Software, including and without
              limitation, for the purpose of obtaining unauthorized access to
              our Services. Lastly, you also agree not to access or attempt to
              access our Services through any means other than through the
              interface which is provided by Space Maintainers Labs, Inc. for
              use in accessing our Services.
            </p>
          </div>
        </section>

        <!-- 17. WARRANTY DISCLAIMERS -->
        <section id="warranty-disclaimers" class="legal-card warning-card">
          <div class="card-header">
            <div class="section-tag tag-warning">Section 17</div>
            <h2>Warranty Disclaimers</h2>
          </div>
          <div class="card-content">
            <p class="caps-bold-lead">
              YOU HEREIN EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
            </p>
            <ol class="styled-numbered-list">
              <li>
                THE USE OF SPACE MAINTAINERS LABS, INC. SERVICES AND SOFTWARE
                ARE AT THE SOLE RISK BY YOU. OUR SERVICES AND SOFTWARE SHALL BE
                PROVIDED ON AN "AS IS" AND/OR "AS AVAILABLE" BASIS. SPACE
                MAINTAINERS LABS, INC. AND OUR SUBSIDIARIES, AFFILIATES,
                OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND LICENSORS EXPRESSLY
                DISCLAIM ANY AND ALL WARRANTIES OF ANY KIND WHETHER EXPRESSED OR
                IMPLIED, INCLUDING, BUT NOT LIMITED TO ANY IMPLIED WARRANTIES OF
                TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
                NON-INFRINGEMENT.
              </li>
              <li>
                SPACE MAINTAINERS LABS, INC. AND OUR SUBSIDIARIES, OFFICERS,
                EMPLOYEES, AGENTS, PARTNERS AND LICENSORS MAKE NO SUCH
                WARRANTIES THAT (i) SPACE MAINTAINERS LABS, INC. SERVICES OR
                SOFTWARE WILL MEET YOUR REQUIREMENTS; (ii) SPACE MAINTAINERS
                LABS, INC. SERVICES OR SOFTWARE SHALL BE UNINTERRUPTED, TIMELY,
                SECURE OR ERROR-FREE; (iii) THAT SUCH RESULTS WHICH MAY BE
                OBTAINED FROM THE USE OF THE SPACE MAINTAINERS LABS, INC.
                SERVICES OR SOFTWARE WILL BE ACCURATE OR RELIABLE; (iv) QUALITY
                OF ANY PRODUCTS, SERVICES, ANY INFORMATION OR OTHER MATERIAL
                WHICH MAY BE PURCHASED OR OBTAINED BY YOU THROUGH OUR SERVICES
                OR SOFTWARE WILL MEET YOUR EXPECTATIONS; AND (v) THAT ANY SUCH
                ERRORS CONTAINED IN THE SOFTWARE SHALL BE CORRECTED.
              </li>
              <li>
                ANY INFORMATION OR MATERIAL DOWNLOADED OR OTHERWISE OBTAINED BY
                WAY OF SPACE MAINTAINERS LABS, INC. SERVICES OR SOFTWARE SHALL
                BE ACCESSED BY YOUR SOLE DISCRETION AND SOLE RISK, AND AS SUCH
                YOU SHALL BE SOLELY RESPONSIBLE FOR AND HEREBY WAIVE ANY AND ALL
                CLAIMS AND CAUSES OF ACTION WITH RESPECT TO ANY DAMAGE TO YOUR
                COMPUTER AND/OR INTERNET ACCESS, DOWNLOADING AND/OR DISPLAYING,
                OR FOR ANY LOSS OF DATA THAT COULD RESULT FROM THE DOWNLOAD OF
                ANY SUCH INFORMATION OR MATERIAL.
              </li>
              <li>
                NO ADVICE AND/OR INFORMATION, DESPITE WHETHER WRITTEN OR ORAL,
                THAT MAY BE OBTAINED BY YOU FROM SPACE MAINTAINERS LABS, INC. OR
                BY WAY OF OR FROM OUR SERVICES OR SOFTWARE SHALL CREATE ANY
                WARRANTY NOT EXPRESSLY STATED IN THE TOS.
              </li>
              <li>
                <strong>EPILEPSY ADVISORY:</strong> A SMALL PERCENTAGE OF SOME
                USERS MAY EXPERIENCE SOME DEGREE OF EPILEPTIC SEIZURE WHEN
                EXPOSED TO CERTAIN LIGHT PATTERNS OR BACKGROUNDS THAT MAY BE
                CONTAINED ON A COMPUTER SCREEN OR WHILE USING OUR SERVICES.
                CERTAIN CONDITIONS MAY INDUCE A PREVIOUSLY UNKNOWN CONDITION OR
                UNDETECTED EPILEPTIC SYMPTOM IN USERS WHO HAVE SHOWN NO HISTORY
                OF ANY PRIOR SEIZURE OR EPILEPSY. SHOULD YOU, ANYONE YOU KNOW OR
                ANYONE IN YOUR FAMILY HAVE AN EPILEPTIC CONDITION, PLEASE
                CONSULT A PHYSICIAN IF YOU EXPERIENCE ANY OF THE FOLLOWING
                SYMPTOMS WHILE USING OUR SERVICES: DIZZINESS, ALTERED VISION,
                EYE OR MUSCLE TWITCHES, LOSS OF AWARENESS, DISORIENTATION, ANY
                INVOLUNTARY MOVEMENT, OR CONVULSIONS.
              </li>
            </ol>
          </div>
        </section>

        <!-- 18. LIMITATION OF LIABILITY & RELEASE -->
        <section id="limitation-liability" class="legal-card warning-card">
          <div class="card-header">
            <div class="section-tag tag-warning">Section 18</div>
            <h2>Limitation of Liability &amp; Release</h2>
          </div>
          <div class="card-content">
            <p class="caps-bold-lead">
              YOU EXPLICITLY ACKNOWLEDGE, UNDERSTAND AND AGREE THAT SPACE
              MAINTAINERS LABS, INC. AND OUR SUBSIDIARIES, AFFILIATES, OFFICERS,
              EMPLOYEES, AGENTS, PARTNERS AND LICENSORS SHALL NOT BE LIABLE TO
              YOU FOR ANY PUNITIVE, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL
              OR EXEMPLARY DAMAGES, INCLUDING, BUT NOT LIMITED TO, DAMAGES WHICH
              MAY BE RELATED TO THE LOSS OF ANY PROFITS, GOODWILL, USE, DATA
              AND/OR OTHER INTANGIBLE LOSSES, EVEN THOUGH WE MAY HAVE BEEN
              ADVISED OF SUCH POSSIBILITY THAT SAID DAMAGES MAY OCCUR, AND
              RESULT FROM:
            </p>
            <ul class="styled-list uppercase-items">
              <li>The use or inability to use our service;</li>
              <li>The cost of procuring substitute goods and services;</li>
              <li>
                Unauthorized access to or the alteration of your transmissions
                and/or data;
              </li>
              <li>
                Statements or conduct of any such third party on our service;
              </li>
              <li>And any other matter which may be related to our service.</li>
            </ul>

            <h3 class="q-mt-lg">Release</h3>
            <p>
              In the event you have a dispute, you agree to release Space
              Maintainers Labs, Inc. (and its officers, directors, employees,
              agents, parent subsidiaries, affiliates, co-branders, partners and
              any other third parties) from claims, demands and damages (actual
              and consequential) of every kind and nature, known and unknown,
              suspected or unsuspected, disclosed and undisclosed, arising out
              of or in any way connected to such dispute.
            </p>
          </div>
        </section>

        <!-- 19. SPECIAL ADMONITION RELATED TO FINANCIAL MATTERS -->
        <section id="financial-admonition" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 19</div>
            <h2>Special Admonition Related to Financial Matters</h2>
          </div>
          <div class="card-content">
            <p>
              Should you intend to create or to join any service, receive or
              request any such news, messages, alerts or other information from
              our Services concerning companies, stock quotes, investments or
              securities, please review the above Sections Warranty Disclaimers
              and Limitations of Liability again.
            </p>
            <p>
              In addition, for this particular type of information, the phrase
              "<strong>Let the investor beware</strong>" is appropriate. Space
              Maintainers Labs, Inc.'s content is provided primarily for
              informational purposes, and no content that shall be provided or
              included in our Services is intended for trading or investing
              purposes.
            </p>
            <p>
              Space Maintainers Labs, Inc. and our licensors shall not be
              responsible or liable for the accuracy, usefulness or availability
              of any information transmitted and/or made available by way of our
              Services, and shall not be responsible or liable for any trading
              and/or investment decisions based on any such information.
            </p>
          </div>
        </section>

        <!-- 20. EXCLUSION AND LIMITATIONS -->
        <section id="exclusions" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 20</div>
            <h2>Exclusion and Limitations</h2>
          </div>
          <div class="card-content">
            <p>
              THERE ARE SOME JURISDICTIONS WHICH DO NOT ALLOW THE EXCLUSION OF
              CERTAIN WARRANTIES OR THE LIMITATION OF EXCLUSION OF LIABILITY FOR
              INCIDENTAL OR CONSEQUENTIAL DAMAGES. THEREFORE, SOME OF THE ABOVE
              LIMITATIONS OF SECTIONS WARRANTY DISCLAIMERS AND LIMITATION OF
              LIABILITY MAY NOT APPLY TO YOU.
            </p>
          </div>
        </section>

        <!-- 21. THIRD PARTY BENEFICIARIES & NOTICE -->
        <section id="notices-beneficiaries" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 21</div>
            <h2>Third Party Beneficiaries &amp; Notice</h2>
          </div>
          <div class="card-content">
            <h3>Third Party Beneficiaries</h3>
            <p>
              You herein acknowledge, understand and agree, unless otherwise
              expressly provided in this TOS, that there shall be no third-party
              beneficiaries to this agreement.
            </p>

            <h3 class="q-mt-md">Notice</h3>
            <p>
              Space Maintainers Labs, Inc. may furnish you with notices,
              including those with regards to any changes to the TOS, including
              but not limited to email, regular mail, MMS or SMS, text
              messaging, postings on our website Services, or other reasonable
              means currently known or any which may be herein after developed.
            </p>
            <p>
              Any such notices may not be received if you violate any aspects of
              the TOS by accessing our Services in an unauthorized manner. Your
              acceptance of this TOS constitutes your agreement that you are
              deemed to have received any and all notices that would have been
              delivered had you accessed our Services in an authorized manner.
            </p>
          </div>
        </section>

        <!-- 22. TRADEMARK INFORMATION -->
        <section id="trademarks" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 22</div>
            <h2>Trademark Information</h2>
          </div>
          <div class="card-content">
            <p>
              You herein acknowledge, understand and agree that all of the Space
              Maintainers Labs, Inc. trademarks, copyright, trade name, service
              marks, and other Space Maintainers Labs, Inc. logos and any brand
              features, and/or product and service names are trademarks and as
              such, are and shall remain the property of Space Maintainers Labs,
              Inc..
            </p>
            <p>
              You herein agree not to display and/or use in any manner the Space
              Maintainers Labs, Inc. logo or marks without obtaining Space
              Maintainers Labs, Inc.'s prior written consent.
            </p>
          </div>
        </section>

        <!-- 23. COPYRIGHT OR INTELLECTUAL PROPERTY INFRINGEMENT CLAIMS -->
        <section id="copyright-claims" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 23</div>
            <h2>
              Copyright or Intellectual Property Infringement Claims Notice
              &amp; Procedures
            </h2>
          </div>
          <div class="card-content">
            <p>
              Space Maintainers Labs, Inc. will always respect the intellectual
              property of others, and we ask that all of our users do the same.
              With regards to appropriate circumstances and at its sole
              discretion, Space Maintainers Labs, Inc. may disable and/or
              terminate the accounts of any user who violates our TOS and/or
              infringes the rights of others.
            </p>
            <p>
              If you feel that your work has been duplicated in such a way that
              would constitute copyright infringement, or if you believe your
              intellectual property rights have been otherwise violated, you
              should provide to us the following information:
            </p>
            <ul class="styled-list">
              <li>
                The electronic or the physical signature of the individual that
                is authorized on behalf of the owner of the copyright or other
                intellectual property interest;
              </li>
              <li>
                A description of the copyrighted work or other intellectual
                property that you believe has been infringed upon;
              </li>
              <li>
                A description of the location of the site which you allege has
                been infringing upon your work;
              </li>
              <li>
                Your physical address, telephone number, and email address;
              </li>
              <li>
                A statement, in which you state that the alleged and disputed
                use of your work is not authorized by the copyright owner, its
                agents or the law;
              </li>
              <li>
                And finally, a statement, made under penalty of perjury, that
                the aforementioned information in your notice is truthful and
                accurate, and that you are the copyright or intellectual
                property owner, representative or agent authorized to act on the
                copyright or intellectual property owner's behalf.
              </li>
            </ul>

            <div class="contact-card-box">
              <h4>Designated Agent for Copyright Notices</h4>
              <p class="contact-name">
                <strong>John Doe</strong>
              </p>
              <div class="contact-details">
                <div class="detail-row">
                  <span class="label">Mailing Address:</span>
                  <span class="value"
                    >Space Maintainers Labs, Inc.<br />
                    9129 Lurline Avenue, Chatsworth, California 91311</span
                  >
                </div>
                <div class="detail-row">
                  <span class="label">Telephone:</span>
                  <a href="tel:8004233270" class="value link">(800) 423-3270</a>
                </div>
                <div class="detail-row">
                  <span class="label">Fax:</span>
                  <span class="value">(818) 341-4684</span>
                </div>
                <div class="detail-row">
                  <span class="label">Email:</span>
                  <div class="value link">JohnDoe@smlglobal.com</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- 24. GENERAL INFORMATION -->
        <section id="general-info" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 24</div>
            <h2>General Information</h2>
          </div>
          <div class="card-content">
            <h3>Entire Agreement</h3>
            <p>
              This TOS constitutes the entire agreement between you and Space
              Maintainers Labs, Inc. and shall govern the use of our Services,
              superseding any prior version of this TOS between you and us with
              respect to Space Maintainers Labs, Inc. Services. You may also be
              subject to additional terms and conditions that may apply when you
              use or purchase certain other Space Maintainers Labs, Inc.
              Services, affiliate Services, third-party content or third-party
              software.
            </p>

            <h3 class="q-mt-md">Choice of Law and Forum</h3>
            <p>
              It is at the mutual agreement of both you and Space Maintainers
              Labs, Inc. with regard to the TOS that the relationship between
              the parties shall be governed by the laws of the state of
              California without regard to its conflict of law provisions and
              that any and all claims, causes of action and/or disputes, arising
              out of or relating to the TOS, or the relationship between you and
              Space Maintainers Labs, Inc., shall be filed within the courts
              having jurisdiction within the County of Los Angeles, California
              or the U.S. District Court located in said state.
            </p>
            <p>
              You and Space Maintainers Labs, Inc. agree to submit to the
              jurisdiction of the courts as previously mentioned, and agree to
              waive any and all objections to the exercise of jurisdiction over
              the parties by such courts and to venue in such courts.
            </p>

            <h3 class="q-mt-md">Waiver and Severability of Terms</h3>
            <p>
              At any time, should Space Maintainers Labs, Inc. fail to exercise
              or enforce any right or provision of the TOS, such failure shall
              not constitute a waiver of such right or provision. If any
              provision of this TOS is found by a court of competent
              jurisdiction to be invalid, the parties nevertheless agree that
              the court should endeavor to give effect to the parties'
              intentions as reflected in the provision, and the other provisions
              of the TOS remain in full force and effect.
            </p>

            <h3 class="q-mt-md">
              No Right of Survivorship &amp; Non-Transferability
            </h3>
            <p>
              You acknowledge, understand and agree that your account is
              non-transferable and any rights to your ID and/or contents within
              your account shall terminate upon your death. Upon receipt of a
              copy of a death certificate, your account may be terminated and
              all contents therein permanently deleted.
            </p>

            <h3 class="q-mt-md">Statute of Limitations</h3>
            <p>
              You acknowledge, understand and agree that regardless of any
              statute or law to the contrary, any claim or action arising out of
              or related to the use of our Services or the TOS must be filed
              within <strong>7 year(s)</strong> after said claim or cause of
              action arose or shall be forever barred.
            </p>
          </div>
        </section>

        <!-- 25. VIOLATIONS -->
        <section id="violations" class="legal-card">
          <div class="card-header">
            <div class="section-tag">Section 25</div>
            <h2>Reporting Violations</h2>
          </div>
          <div class="card-content">
            <p>
              Please report any and all violations of this TOS to Space
              Maintainers Labs, Inc. as follows:
            </p>

            <div class="contact-card-box highlight-contact">
              <h4>Space Maintainers Labs, Inc. Compliance Department</h4>
              <p class="contact-name">
                <strong>Victor Paraza</strong>
              </p>
              <div class="contact-details">
                <div class="detail-row">
                  <span class="label">Mailing Address:</span>
                  <span class="value"
                    >Space Maintainers Labs, Inc.<br />
                    9129 Lurline Avenue, Chatsworth, California 91311</span
                  >
                </div>
                <div class="detail-row">
                  <span class="label">Telephone:</span>
                  <a href="tel:8004233270" class="value link">(800) 423-3270</a>
                </div>
                <div class="detail-row">
                  <span class="label">Fax:</span>
                  <span class="value">(818) 341-4684</span>
                </div>
                <div class="detail-row">
                  <span class="label">Email:</span>
                  <span class="value link">johndoe@smlglobal.com</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Back to top banner -->
        <div class="back-to-top-card">
          <div>
            <h3>Questions about our Terms?</h3>
            <p>Our legal and customer support teams are here to help you.</p>
          </div>
          <div class="btn-group">
            <button
              class="btn btn-outline"
              @click="scrollToSection('acceptance')"
            >
              ↑ Back to Top
            </button>
            <router-link to="/" class="btn btn-primary">
              Return to Store
            </router-link>
          </div>
        </div>
      </main>
    </div>

    <!-- ================= FOOTER ================= -->
    <footer class="text-center">
      © Space Maintainers Labs, Inc. (SML Global). All rights reserved. SML and
      Oraklenz are trademarks of Space Maintainers Labs, Inc. 9129 Lurline
      Avenue, Chatsworth, California 91311.
    </footer>

    <!-- ================= CART DRAWER ================= -->
    <q-dialog
      v-model="isCartOpen"
      position="right"
      maximized
      @hide="handleDialogHide"
    >
      <q-card class="column full-height" style="width: 400px; max-width: 100vw">
        <q-card-section class="row items-center q-pb-none q-pt-md q-px-md">
          <div class="text-h5 text-weight-bold flex items-center">
            <q-btn
              v-if="isCheckoutStep"
              icon="arrow_back"
              flat
              round
              dense
              class="q-mr-sm"
              @click="isCheckoutStep = false"
            />
            {{ isCheckoutStep ? "Checkout" : "Your Cart" }}
          </div>
          <q-space />
          <q-btn v-close-popup icon="close" flat round dense />
        </q-card-section>

        <q-card-section class="col q-pt-lg q-px-md scroll">
          <div v-if="!isCheckoutStep">
            <div
              v-if="cartStore.items.length === 0"
              class="text-center text-grey-7 q-pa-md"
            >
              Your cart is empty.
            </div>
            <template v-else>
              <q-list>
                <q-item
                  v-for="l in cartStore.items || []"
                  :key="l.key"
                  class="q-px-none q-mb-md items-start"
                >
                  <q-item-section avatar>
                    <div class="cart-line-thumb">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#0077be"
                        stroke-width="2"
                      >
                        <path
                          d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"
                        />
                        <line x1="3" y1="6" x2="21" y2="6" />
                        <path d="M16 10a4 4 0 0 1-8 0" />
                      </svg>
                    </div>
                  </q-item-section>
                  <q-item-section>
                    <q-item-label class="text-weight-bold">
                      {{ l.name }}
                    </q-item-label>
                    <q-item-label caption class="q-mt-xs">
                      Qty: {{ l.qty }} × ${{ l.price.toFixed(2) }}
                    </q-item-label>
                    <q-item-label>
                      <span
                        class="text-negative text-caption cursor-pointer"
                        @click="removeFromCart(l.key)"
                      >
                        Remove
                      </span>
                    </q-item-label>
                  </q-item-section>
                  <q-item-section side top>
                    <div class="text-weight-bold text-body2">
                      ${{ (l.price * l.qty).toFixed(2) }}
                    </div>
                  </q-item-section>
                </q-item>
              </q-list>
            </template>
          </div>

          <div v-else>
            <q-form class="q-gutter-md">
              <q-input
                v-model="checkoutForm.name"
                outlined
                label="Full Name"
                dense
              />
              <q-input
                v-model="checkoutForm.email"
                outlined
                label="Email Address"
                type="email"
                dense
              />
              <q-input
                v-model="checkoutForm.address"
                outlined
                label="Shipping Address"
                dense
              />
              <q-input
                v-model="checkoutForm.card"
                outlined
                label="Card Number"
                placeholder="**** **** **** ****"
                dense
              />
              <div class="row q-col-gutter-sm">
                <div class="col-6">
                  <q-input
                    v-model="checkoutForm.exp"
                    outlined
                    label="MM/YY"
                    dense
                  />
                </div>
                <div class="col-6">
                  <q-input
                    v-model="checkoutForm.cvv"
                    outlined
                    label="CVV"
                    dense
                  />
                </div>
              </div>
            </q-form>
          </div>
        </q-card-section>

        <q-separator />

        <q-card-actions class="column q-pa-md">
          <div
            class="row full-width justify-between q-mb-md text-weight-bold text-h6"
          >
            <span>Subtotal</span>
            <span>${{ (cartStore.subtotal || 0).toFixed(2) }}</span>
          </div>
          <q-btn
            v-if="!isCheckoutStep"
            color="dark"
            class="full-width q-py-sm text-weight-bold"
            unelevated
            label="Proceed to Checkout"
            @click="checkout()"
          />
          <q-btn
            v-else
            color="primary"
            class="full-width q-py-sm text-weight-bold"
            unelevated
            label="Complete Order"
            @click="completeOrder()"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<style scoped>
/* Scoped & Theme Styles */
.legal-page {
  --ink: #002244;
  --ink-soft: #4a6b8c;
  --paper: #ffffff;
  --paper-raised: #f7fafc;
  --teal-deep: #00437c;
  --teal-bright: #0077be;
  --teal-pale: #e6f0f9;
  --coral: #0077be;
  --coral-dark: #00437c;
  --line: #d1e3f0;
  --line-subtle: #e8f0f6;
  --gold: #c9a24b;
  --warning-bg: #fffbf0;
  --warning-border: #fce8b2;
  --warning-text: #8c5b00;
  --radius: 14px;
  --maxw: 1220px;
  --font-display: "Fraunces", serif;
  --font-body: "Inter", sans-serif;
  --font-mono: "IBM Plex Mono", monospace;

  background: #fbfdfe;
  color: var(--ink);
  font-family: var(--font-body);
  line-height: 1.6;
}

.wrap {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 0 24px;
}

.brand-link {
  text-decoration: none;
}

/* Announcement Bar */
.announce {
  background: var(--teal-deep);
  color: #eaf6f3;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 12.5px;
  letter-spacing: 0.03em;
  padding: 9px 12px;
}

.announce b {
  color: #fff;
}

/* Header */
header {
  position: sticky;
  top: 0;
  z-index: 50;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--line);
}

.nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  max-width: var(--maxw);
  margin: 0 auto;
}

.navlinks {
  display: flex;
  align-items: center;
  gap: 26px;
  font-size: 14.5px;
  font-weight: 500;
}

.navlinks a {
  text-decoration: none;
  color: var(--ink-soft);
  transition: color 0.15s ease;
}

.navlinks a:hover {
  color: var(--teal-bright);
}

.navlinks .active-nav {
  color: var(--teal-deep);
  font-weight: 600;
  background: var(--teal-pale);
  padding: 4px 12px;
  border-radius: 999px;
}

.navright {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cart-btn {
  position: relative;
  background: none;
  border: 1.5px solid var(--line);
  border-radius: 999px;
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.cart-btn:hover {
  background-color: var(--teal-pale);
  border-color: var(--teal-bright);
}

.cart-count {
  position: absolute;
  top: -6px;
  right: -6px;
  background: var(--coral);
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  width: 19px;
  height: 19px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: var(--font-mono);
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 14.5px;
  padding: 10px 22px;
  border-radius: 999px;
  text-decoration: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.btn-primary {
  background: var(--coral);
  color: #fff;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 119, 190, 0.3);
}

.btn-outline {
  background: transparent;
  color: var(--teal-deep);
  border: 1.5px solid var(--teal-deep);
}

.btn-outline:hover {
  background: var(--teal-deep);
  color: #fff;
}

@media (max-width: 860px) {
  .navlinks {
    display: none;
  }
}

/* Legal Hero */
.legal-hero {
  background: linear-gradient(180deg, #f0f7fc 0%, #fbfdfe 100%);
  padding: 38px 0 28px;
  border-bottom: 1px solid var(--line-subtle);
}

.breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13.5px;
  color: var(--ink-soft);
  margin-bottom: 16px;
}

.breadcrumbs a {
  color: var(--ink-soft);
  text-decoration: none;
}

.breadcrumbs a:hover {
  color: var(--teal-bright);
  text-decoration: underline;
}

.breadcrumbs .separator {
  color: #9cb5cb;
}

.breadcrumbs .current {
  color: var(--teal-deep);
  font-weight: 600;
}

.hero-content {
  max-width: 800px;
}

.eyebrow {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--teal-deep);
  font-weight: 600;
  margin-bottom: 8px;
}

.page-title {
  font-family: var(--font-display);
  font-size: clamp(28px, 4vw, 42px);
  font-weight: 700;
  line-height: 1.15;
  color: var(--ink);
  margin: 0 0 12px;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 17px;
  color: var(--ink-soft);
  margin: 0 0 20px;
  line-height: 1.5;
}

.meta-badge-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.meta-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid var(--line);
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 13px;
  color: var(--ink-soft);
  box-shadow: 0 2px 5px rgba(0, 34, 68, 0.03);
}

.meta-badge strong {
  color: var(--ink);
}

.meta-badge svg {
  color: var(--teal-bright);
}

/* Notice Callout */
.notice-callout {
  background: #f0f7ff;
  border: 1px solid #c8e1f5;
  border-left: 4px solid #0077be;
  border-radius: 10px;
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.callout-icon {
  color: #0077be;
  flex-shrink: 0;
  margin-top: 2px;
}

.callout-text {
  font-size: 14.5px;
  line-height: 1.55;
  color: #1a4268;
}

/* Layout Grid */
.legal-body-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  padding-top: 20px;
  padding-bottom: 60px;
  align-items: start;
}

@media (max-width: 980px) {
  .legal-body-layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .legal-sidebar {
    position: static !important;
    top: auto !important;
  }
}

/* Sidebar TOC */
.legal-sidebar {
  position: sticky;
  top: 90px;
  max-height: calc(100vh - 110px);
  display: flex;
  flex-direction: column;
}

.sidebar-inner {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 18px;
  box-shadow: 0 4px 16px rgba(0, 34, 68, 0.04);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 120px);
}

.sidebar-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-mono);
  font-size: 12.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--teal-deep);
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line-subtle);
  margin-bottom: 10px;
}

.toc-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  padding-right: 4px;
  max-height: 380px;
}

.toc-link {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  text-align: left;
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 13px;
  color: var(--ink-soft);
  cursor: pointer;
  transition: all 0.15s ease;
  width: 100%;
}

.toc-link:hover {
  background: var(--teal-pale);
  color: var(--teal-deep);
}

.toc-link.active {
  background: var(--teal-pale);
  color: var(--teal-deep);
  font-weight: 600;
  border-left: 3px solid var(--teal-bright);
}

.toc-num {
  font-family: var(--font-mono);
  font-size: 11px;
  color: #7b9ab8;
  flex-shrink: 0;
}

.toc-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sidebar-contact-card {
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid var(--line-subtle);
}

.sidebar-contact-card h4 {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 4px;
}

.sidebar-contact-card p {
  font-size: 12px;
  color: var(--ink-soft);
  margin: 0 0 10px;
  line-height: 1.4;
}

.sidebar-contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--teal-pale);
  color: var(--teal-deep);
  font-size: 12px;
  font-weight: 600;
  padding: 7px 12px;
  border-radius: 6px;
  text-decoration: none;
  width: 100%;
  justify-content: center;
  transition: background 0.15s ease;
}

.sidebar-contact-btn:hover {
  background: #d4e7f7;
}

/* Legal Cards */
.legal-content {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.legal-card {
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 32px;
  box-shadow: 0 2px 10px rgba(0, 34, 68, 0.03);
  scroll-margin-top: 100px;
}

.legal-card.warning-card {
  border-color: #f1dfb8;
  background: linear-gradient(180deg, #fffdf8 0%, #ffffff 100%);
}

.card-header {
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--line-subtle);
}

.section-tag {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--teal-bright);
  background: var(--teal-pale);
  padding: 3px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.section-tag.tag-warning {
  color: #946200;
  background: #feefc3;
}

.legal-card h2 {
  font-family: var(--font-display);
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  margin: 0;
  letter-spacing: -0.01em;
}

.legal-card h3 {
  font-family: var(--font-body);
  font-size: 16.5px;
  font-weight: 700;
  color: var(--ink);
  margin: 20px 0 8px;
}

.legal-card p {
  font-size: 15px;
  color: #27415e;
  margin: 0 0 14px;
  line-height: 1.65;
}

.legal-card a {
  color: var(--teal-bright);
  font-weight: 500;
  text-decoration: underline;
}

.legal-card a:hover {
  color: var(--teal-deep);
}

.company-address-box {
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--teal-pale);
  border: 1px solid #cce3f5;
  border-radius: 10px;
  padding: 14px 18px;
  margin: 16px 0 20px;
  color: var(--teal-deep);
  font-size: 14.5px;
}

.highlight-quote {
  margin: 14px 0 18px;
  padding: 12px 18px;
  background: #f4f8fb;
  border-left: 4px solid var(--teal-bright);
  border-radius: 0 8px 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ink);
  font-style: italic;
}

.styled-list {
  margin: 10px 0 18px 20px;
  padding: 0;
  color: #27415e;
  font-size: 14.5px;
}

.styled-list li {
  margin-bottom: 8px;
  line-height: 1.6;
}

.styled-numbered-list {
  margin: 14px 0 20px 22px;
  padding: 0;
  color: #27415e;
  font-size: 14px;
}

.styled-numbered-list li {
  margin-bottom: 12px;
  line-height: 1.6;
}

.caps-bold-lead {
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 0.02em;
  color: var(--ink);
  margin-bottom: 14px;
}

.sub-alert-box {
  background: #fbfcfe;
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 18px 20px;
  margin-top: 18px;
}

.sub-alert-box h4 {
  font-size: 15px;
  font-weight: 700;
  color: var(--teal-deep);
  margin: 0 0 8px;
}

.sub-alert-box p {
  font-size: 14px;
  margin-bottom: 10px;
}

.sub-alert-box p:last-child {
  margin-bottom: 0;
}

.contact-card-box {
  background: #f4f8fc;
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 22px 24px;
  margin: 20px 0 10px;
}

.contact-card-box.highlight-contact {
  background: #edf5fc;
  border-color: #b9daf2;
}

.contact-card-box h4 {
  font-size: 16px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 6px;
}

.contact-name {
  font-size: 15px;
  color: var(--teal-deep);
  margin-bottom: 14px !important;
}

.contact-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.detail-row {
  display: grid;
  grid-template-columns: 140px 1fr;
  font-size: 14px;
}

@media (max-width: 600px) {
  .detail-row {
    grid-template-columns: 1fr;
    gap: 2px;
  }
}

.detail-row .label {
  font-weight: 600;
  color: var(--ink-soft);
}

.detail-row .value {
  color: var(--ink);
}

.detail-row .link {
  color: var(--teal-bright);
  font-weight: 600;
}

.back-to-top-card {
  background: var(--paper-raised);
  border: 1px solid var(--line);
  border-radius: var(--radius);
  padding: 28px 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.back-to-top-card h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--ink);
  margin: 0 0 4px;
}

.back-to-top-card p {
  font-size: 14px;
  color: var(--ink-soft);
  margin: 0;
}

.back-to-top-card .btn-group {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

@media (max-width: 680px) {
  .back-to-top-card {
    flex-direction: column;
    text-align: center;
  }
  .back-to-top-card .btn-group {
    width: 100%;
    justify-content: center;
  }
}

/* Footer */
footer {
  background: var(--ink);
  color: #d1e3f0;
  padding: 52px 0 28px;
  margin-top: 40px;
}

.footer-grid {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 32px;
}

@media (max-width: 760px) {
  .footer-grid {
    grid-template-columns: 1fr 1fr;
  }
}

footer h4 {
  color: #fff;
  font-size: 13px;
  font-family: var(--font-mono);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  margin: 0 0 14px;
}

footer a {
  display: block;
  color: #d1e3f0;
  text-decoration: none;
  font-size: 13.5px;
  margin-bottom: 9px;
  transition: color 0.15s ease;
}

footer a:hover {
  color: #fff;
}

.disclaimer {
  font-size: 11.5px;
  color: #7292b3;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 36px;
  padding-top: 22px;
  line-height: 1.6;
}

/* Cart Thumb */
.cart-line-thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  background: var(--teal-pale);
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
